import {
  LayoutGeometryHelper as G,
  type NodeFrame,
} from './layout-geometry-helper';
import type { Point } from './structure-layout.types';

export interface LeafInput {
  entityId: number;
  footprintHeight: number;
}

export type LeafMode = 'single' | 'split' | 'pairs';

export type LeafSide = 'left' | 'right';

export interface LeafPlacement {
  entityId: number;
  x: number;
  y: number;
  side: LeafSide;
  trunkX: number;
  column: number;
}

// Relative to the parent's trunk at x = 0, with the first row (or the pair bus) at y = 0.
export interface LeafArea {
  mode: LeafMode;
  placements: LeafPlacement[];
  height: number;
  columnCount: number;
  corridors: number[];
}

class LeafColumnHelper {
  static getMode(count: number): LeafMode {
    if (count <= G.SPLIT_AT) return 'single';
    if (count <= G.MAX_ROWS * 2) return 'split';
    return 'pairs';
  }

  static isInOutermostRightColumn(count: number, index: number): boolean {
    const mode = LeafColumnHelper.getMode(count);
    if (mode === 'single') return true;
    if (mode === 'split') return index >= Math.ceil(count / 2);
    return index >= (Math.ceil(count / G.MAX_ROWS) - 1) * G.MAX_ROWS;
  }

  static arrange(
    leaves: ReadonlyArray<LeafInput>,
    labelIndent: number,
    frame: Readonly<Pick<NodeFrame, 'width' | 'rowGap'>> = G.CARD_FRAME
  ): LeafArea {
    const mode = LeafColumnHelper.getMode(leaves.length);
    const indent = Math.max(
      mode === 'single' ? G.MIN_INDENT : G.SPLIT_MIN_INDENT,
      labelIndent
    );
    if (mode === 'single')
      return LeafColumnHelper.stack(
        mode,
        [{ leaves, side: 'right', trunkX: 0, top: 0 }],
        indent,
        frame,
        [0]
      );
    if (mode === 'split') {
      const leftCount = Math.ceil(leaves.length / 2);
      return LeafColumnHelper.stack(
        mode,
        [
          {
            leaves: leaves.slice(0, leftCount),
            side: 'left',
            trunkX: 0,
            top: 0,
          },
          { leaves: leaves.slice(leftCount), side: 'right', trunkX: 0, top: 0 },
        ],
        indent,
        frame,
        [0]
      );
    }
    return LeafColumnHelper.arrangePairs(leaves, indent, frame);
  }

  static getBranchPoints(
    placement: Readonly<LeafPlacement>,
    area: Readonly<LeafArea>,
    frame: Readonly<NodeFrame> = G.CARD_FRAME
  ): Point[] {
    const centerY = placement.y + frame.height / 2;
    const inset = frame.sideInsetOf(placement.entityId);
    const edgeX =
      placement.side === 'right'
        ? placement.x + inset
        : placement.x + frame.width - inset;
    const bus = area.mode === 'pairs' ? [{ x: placement.trunkX, y: 0 }] : [];
    return [
      { x: 0, y: 0 },
      ...bus,
      { x: placement.trunkX, y: centerY },
      { x: edgeX, y: centerY },
    ];
  }

  private static arrangePairs(
    leaves: ReadonlyArray<LeafInput>,
    indent: number,
    frame: Readonly<Pick<NodeFrame, 'width' | 'rowGap'>>
  ): LeafArea {
    const columnCount = Math.ceil(leaves.length / G.MAX_ROWS);
    const pairCount = Math.ceil(columnCount / 2);
    const reach = indent + frame.width;
    let cursor = 0;
    const trunks = Array.from({ length: pairCount }, (_unused, pair) => {
      const hasLeft = pair * 2 + 1 < columnCount;
      const trunkX = cursor + (hasLeft ? reach : 0);
      cursor = trunkX + reach + G.PAIR_GAP;
      return trunkX;
    });
    const shift = -Math.round((trunks[0] + trunks[trunks.length - 1]) / 2);
    const columns = Array.from({ length: columnCount }, (_unused, column) => {
      const pair = Math.floor(column / 2);
      const isLastAlone = column === columnCount - 1 && columnCount % 2 === 1;
      const side: LeafSide =
        column % 2 === 0 && !isLastAlone ? 'left' : 'right';
      return {
        leaves: leaves.slice(column * G.MAX_ROWS, (column + 1) * G.MAX_ROWS),
        side,
        trunkX: trunks[pair] + shift,
        top: G.PAIR_BUS_DROP,
      };
    });
    const shiftedTrunks = trunks.map((x) => x + shift);
    const leftEdgeOf = (pair: number) =>
      shiftedTrunks[pair] - (pair * 2 + 1 < columnCount ? reach : 0);
    const gaps = shiftedTrunks
      .slice(1)
      .map((_x, i) =>
        Math.round((shiftedTrunks[i] + reach + leftEdgeOf(i + 1)) / 2)
      );
    return LeafColumnHelper.stack('pairs', columns, indent, frame, [
      ...shiftedTrunks,
      ...gaps,
    ]);
  }

  private static stack(
    mode: LeafMode,
    columns: ReadonlyArray<{
      leaves: ReadonlyArray<LeafInput>;
      side: LeafSide;
      trunkX: number;
      top: number;
    }>,
    indent: number,
    frame: Readonly<Pick<NodeFrame, 'width' | 'rowGap'>>,
    corridors: number[]
  ): LeafArea {
    const placements: LeafPlacement[] = [];
    let height = 0;
    columns.forEach((column, columnIndex) => {
      let y = column.top;
      const x =
        column.side === 'right'
          ? column.trunkX + indent
          : column.trunkX - indent - frame.width;
      column.leaves.forEach((leaf, row) => {
        placements.push({
          entityId: leaf.entityId,
          x,
          y,
          side: column.side,
          trunkX: column.trunkX,
          column: columnIndex,
        });
        y +=
          leaf.footprintHeight +
          (row < column.leaves.length - 1 ? frame.rowGap : 0);
      });
      height = Math.max(height, y);
    });
    return {
      mode,
      placements,
      height,
      columnCount: columns.length,
      corridors,
    };
  }
}

export { LeafColumnHelper };
